import { CheckCircleIcon } from "vega-icons";

import {
  useUploadCoinvestTeaserDocument,
  useViewCoinvestTeaserDocument,
} from "@vega-api/generated/coinvest-deal-teaser-docs/coinvest-deal-teaser-docs";
import { type TeaserResponse } from "@vega-api/generated/types/teaserResponse";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { FormGroup } from "@vega-modules/scenes/CoInvestmentsGP/components/CoInvestmentEditForm/components/FormGroup";

type TeaserInformationSectionProps = {
  dealId: string;
  onTeaserUploaded?: () => void;
  teaserData?: TeaserResponse;
  editable?: boolean;
};

export const TeaserInformationSection = ({
  dealId,
  onTeaserUploaded,
  teaserData,
  editable,
}: TeaserInformationSectionProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();

  const documentId = teaserData?.documentMetaData.documentId;
  const { data: teaserUrl, isLoading: isLoadingDownload } =
    useViewCoinvestTeaserDocument(documentId ?? "", {
      query: {
        enabled: !!documentId,
        select: (data) => data.data?.presignedUrl?.url,
      },
    });

  const uploadTeaserMutation = useUploadCoinvestTeaserDocument({
    mutation: {
      onSuccess: () => {
        showToast({
          variant: "success",
          title: m.status_success(),
          description: m.coInvestments_teaserUploadSuccess(),
        });
        onTeaserUploaded?.();
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.coInvestments_error_uploadTeaser(),
        });
      },
    },
  });

  const onFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      uploadTeaserMutation.mutate({
        data: {
          dealId: dealId,
          file: file,
        },
      });
    }
  };

  const getButtonText = () => {
    if (uploadTeaserMutation.isPending) return m.common_pending();

    if (teaserData) return m.common_update();

    return m.common_upload();
  };

  return (
    <FormGroup
      dataTestId="teaser-information"
      label={m.common_teaserInformation()}
    >
      <div className="flex items-center justify-between p-4 bg-background/primary border border-border rounded-md">
        <div>
          <h3 className="body-base-semibold text-text/primary">
            {m.coInvestments_teaserDocument()}
          </h3>
          <p className="body-small-regular text-text/secondary">
            {m.coInvestments_pdfOnly()}
          </p>
        </div>
        {teaserData && teaserUrl ? (
          <div className="flex items-center gap-1">
            <CheckCircleIcon className="size-4 text-text/pending" />
            <p className="body-small-regular text-text/success">
              <ThemedButton
                className="body-small-regular"
                dataTestId="teaser-document-download-link"
                disabled={isLoadingDownload}
                rel="noopener noreferrer"
                route={teaserUrl}
                target="_blank"
                variant="text"
              >
                {teaserData.documentMetaData.nameOfDocument}
              </ThemedButton>
            </p>
          </div>
        ) : null}

        {editable ? (
          <div className="flex items-center gap-2">
            <input
              accept="application/pdf"
              className="hidden"
              disabled={uploadTeaserMutation.isPending}
              id="teaser-upload"
              onChange={onFileSelect}
              type="file"
            />
            <ThemedButton
              as="label"
              className="min-h-7 h-9 px-3"
              dataTestId="select-teaser-file-button"
              disabled={uploadTeaserMutation.isPending}
              htmlFor="teaser-upload"
              variant="secondary"
            >
              {getButtonText()}
            </ThemedButton>
          </div>
        ) : (
          <div>{/* Dummy for layout purpose only */}</div>
        )}
      </div>
    </FormGroup>
  );
};
